/**
 * DailyEngagementWidget — shows above bulletin announcements.
 * Iter 101: quote / news / trend / tip / fact of the day + reactions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Sparkles, ExternalLink, Settings } from "lucide-react";

const KIND_ACCENT = {
  quote: { bg: "from-indigo-50 to-purple-50", chip: "bg-indigo-100 text-indigo-700", icon: "💭" },
  news: { bg: "from-sky-50 to-cyan-50", chip: "bg-sky-100 text-sky-700", icon: "📰" },
  trend: { bg: "from-emerald-50 to-teal-50", chip: "bg-emerald-100 text-emerald-700", icon: "📈" },
  tip: { bg: "from-amber-50 to-orange-50", chip: "bg-amber-100 text-amber-700", icon: "💡" },
  fact: { bg: "from-fuchsia-50 to-pink-50", chip: "bg-fuchsia-100 text-fuchsia-700", icon: "🧠" },
  custom: { bg: "from-slate-50 to-slate-100", chip: "bg-slate-200 text-slate-700", icon: "✨" },
};

const EMOJIS = ["👍", "🎉", "💡", "❤️", "🔥", "🤔"];

export default function DailyEngagementWidget({ wsId }) {
  const { can } = useWorkspacePerms();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const canManage = can("engagement.manage");

  const load = async () => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/engagement/today`);
      setItem(r.data.item);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [wsId]);
  useEffect(() => {
    if (item && !item._marked) {
      item._marked = true;
      api.post(`/engagement/items/${item.id}/mark-viewed`).catch(() => {});
    }
  }, [item?.id]); // eslint-disable-line

  if (loading) return null;
  if (!item) {
    if (!canManage) return null;
    return (
      <div className="border border-dashed border-border p-3 flex items-center gap-2 text-sm text-muted-foreground"
           data-testid="daily-engagement-empty">
        <Sparkles className="w-4 h-4" />
        <span>Boost engagement — add a quote, news, or trend of the day.</span>
        <Link to="/app/engagement" className="ml-auto text-primary underline text-xs">Configure</Link>
      </div>
    );
  }

  const accent = KIND_ACCENT[item.kind] || KIND_ACCENT.custom;

  const react = async (emoji) => {
    try {
      const r = await api.post(`/engagement/items/${item.id}/react`, { emoji });
      setItem({ ...item, my_reaction: r.data.my_reaction,
                 reaction_summary: r.data.reaction_summary });
    } catch (e) { toast.error(apiErrorMessage(e, "Reaction failed")); }
  };

  const totalReactions = Object.values(item.reaction_summary || {}).reduce((a, b) => a + b, 0);

  return (
    <div className={`border border-border relative overflow-hidden bg-gradient-to-br ${accent.bg}`}
         data-testid="daily-engagement-widget">
      {canManage && (
        <Link to="/app/engagement" data-testid="daily-engagement-configure"
               className="absolute top-2 right-2 text-[10px] flex items-center gap-1 text-muted-foreground hover:text-foreground bg-white/60 backdrop-blur px-1.5 py-0.5">
          <Settings className="w-3 h-3" /> Configure
        </Link>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${accent.chip}`}
                 data-testid="daily-engagement-kind">
            {accent.icon} {item.kind.toUpperCase()} OF THE DAY
          </span>
          {item.tags?.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] text-slate-500 bg-white/40 px-1.5 rounded">#{t}</span>
          ))}
        </div>
        {item.title && (
          <h3 className="text-lg font-bold text-slate-900 mb-1" data-testid="daily-engagement-title">
            {item.title}
          </h3>
        )}
        <div className="prose prose-sm max-w-none text-slate-800"
             data-testid="daily-engagement-body"
             dangerouslySetInnerHTML={{ __html: item.body_html }} />
        {item.author && (
          <div className="text-xs text-slate-500 italic mt-2">— {item.author}</div>
        )}
        {item.source_url && (
          <a href={item.source_url} target="_blank" rel="noopener noreferrer"
             className="text-xs inline-flex items-center gap-1 text-primary underline mt-2"
             data-testid="daily-engagement-source">
            Read more <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Reactions */}
        <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="daily-engagement-reactions">
          {EMOJIS.map((em) => {
            const n = (item.reaction_summary || {})[em] || 0;
            const mine = item.my_reaction === em;
            return (
              <button key={em} onClick={() => react(em)}
                      data-testid={`daily-engagement-react-${em}`}
                      className={`text-sm px-1.5 py-0.5 rounded transition-colors ${
                        mine ? "bg-white shadow-sm ring-1 ring-primary/40"
                             : "bg-white/50 hover:bg-white"
                      }`}>
                {em}{n > 0 && <span className="ml-1 text-[10px] font-mono text-slate-600">{n}</span>}
              </button>
            );
          })}
          {totalReactions > 0 && (
            <span className="text-[10px] text-slate-500 ml-auto">
              {totalReactions} reaction{totalReactions === 1 ? "" : "s"} · {item.view_count || 0} view{item.view_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
